import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Heart, Handshake, ChevronDown, ShieldCheck } from 'lucide-react';
import { useSite } from '@/context/SiteContext';
import { stripHtml } from '@/lib/richContent';

const HeroSection = () => {
    const { settings } = useSite();
    const navigate = useNavigate();
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mq.matches);
        const handler = (e) => setPrefersReducedMotion(e.matches);
        mq.addEventListener?.('change', handler);
        return () => mq.removeEventListener?.('change', handler);
    }, []);

    return (
        <section
            className="relative min-h-[72vh] lg:min-h-[86vh] flex items-center overflow-hidden bg-[hsl(var(--primary))]"
            data-testid="home-hero"
        >
            {/* Video / Poster */}
            <div className="absolute inset-0">
                {!prefersReducedMotion && settings?.hero_video_url ? (
                    <video
                        className="w-full h-full object-cover"
                        src={settings.hero_video_url}
                        poster={settings.hero_poster_url}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        data-testid="hero-video"
                    />
                ) : (
                    <img
                        src={settings?.hero_poster_url}
                        alt="VVVSI community"
                        className="w-full h-full object-cover"
                    />
                )}
                <div className="absolute inset-0 hero-overlay" />
            </div>

            {/* Content */}
            <div className="container-wide relative z-10 py-24 text-white">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl"
                >
                    <div className="flex items-center gap-3 mb-4">
                        <span className="h-[3px] w-10 rounded bg-[hsl(var(--accent))]" />
                        <span className="text-[hsl(var(--accent))] font-semibold text-xs uppercase tracking-widest">
                            {stripHtml(settings?.trust_name) || 'VVVSI'}
                        </span>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                        {stripHtml(settings?.hero_title) ||
                            'Empowering Lives. Building Livelihoods. Serving Communities.'}
                    </h1>
                    {settings?.hero_subtitle && (
                        <p className="mt-5 text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed">
                            {stripHtml(settings.hero_subtitle)}
                        </p>
                    )}

                    <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <Button
                            size="lg"
                            onClick={() => navigate('/donate')}
                            className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold px-6"
                            data-testid="hero-donate-button"
                        >
                            <Heart className="h-4 w-4 mr-2" />
                            Donate Now
                        </Button>
                        <Button
                            size="lg"
                            variant="outline"
                            onClick={() => navigate('/volunteer')}
                            className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white font-semibold"
                            data-testid="hero-volunteer-button"
                        >
                            <Handshake className="h-4 w-4 mr-2" />
                            Volunteer With Us
                        </Button>
                    </div>

                    <div className="mt-6 flex items-center gap-3 text-xs text-white/75">
                        <ShieldCheck className="h-4 w-4 text-[hsl(var(--accent))]" />
                        Registered under 80G, 12A & CSR-1. All donations are tax-exempt.
                    </div>
                </motion.div>
            </div>

            {/* Scroll cue */}
            {!prefersReducedMotion && (
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 animate-bounce z-10">
                    <ChevronDown className="h-6 w-6" />
                </div>
            )}
        </section>
    );
};

export default HeroSection;
